import { animate } from "animejs"
import { noop } from "lodash"
import React, { PropsWithChildren, ReactNode, useEffect, useRef } from "react"
import { createRoot } from "react-dom/client"

import styles from "./index.module.scss"

interface IProps {
    visible?: boolean
    afterClose?: () => void
}

const OriginalToast: React.FC<PropsWithChildren<IProps>> = (props) => {
    const { visible = false, afterClose = noop, children } = props

    const node = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (node.current) {
            animate(node.current, {
                opacity: visible ? [0, 1] : [1, 0],
                duration: 300,
                ease: "cubicBezier(0.25, 0.1, 0.25, 1)",
                onComplete: () => {
                    if (!visible) {
                        afterClose()
                    }
                },
            })
        }
    }, [visible, afterClose])

    return (
        <div ref={node} className={styles.toast}>
            <div className={styles.content}>{children}</div>
        </div>
    )
}

interface IToastOptions {
    duration?: number
}

function message(content: ReactNode, opts: IToastOptions = {}) {
    if (!content) return

    const { duration = 2000 } = opts

    const div = document.createElement("div")
    document.body.appendChild(div)
    const root = createRoot(div)

    function render({
        content,
        ...props
    }: IProps & { content?: React.ReactNode }) {
        setTimeout(() => {
            root.render(<OriginalToast {...props}>{content}</OriginalToast>)
        })
    }

    render({
        visible: true,
        content,
    })

    setTimeout(() => {
        render({
            visible: false,
            content,
            afterClose: () => {
                root.unmount()
                div.remove()
            },
        })
    }, duration)
}

interface ToastStaticFunctions {
    message: (content: ReactNode, opts?: IToastOptions) => void
}

type Toast = typeof OriginalToast & ToastStaticFunctions

const Toast = OriginalToast as Toast
Toast.message = message

export default Toast
